import { DeleteOutlined, HolderOutlined, PlusOutlined } from "@ant-design/icons"
import { DndContext, type DragEndEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Card, Empty, Flex, Form } from "antd"
import React from "react"

import { type ProjectFormDataPhase } from "../../../project-form-data.ts"
import { TextInput } from "../../../../../../../components/Input/TextInput.tsx"

interface MilestoneChecklistProps {
  fieldName: number
}

export function MilestoneChecklist({ fieldName }: MilestoneChecklistProps) {
  return (
    <Card className={"my-2"} classNames={{ body: "p-2" }}>
      <Form.List name={[fieldName, "checklist_data"]}>
        {(fields, { add, move, remove }, { errors }) => {
          const handleDragEndWithMove = (event: DragEndEvent) => {
            const { active, over } = event

            if (!over || active.id === over.id) {
              return
            }

            const activeIndex = parseInt(active.id as string)
            const overIndex = parseInt(over.id as string)

            if (!isNaN(activeIndex) && !isNaN(overIndex)) {
              move(activeIndex, overIndex)
            }
          }

          if (fields.length === 0) {
            return (
              <Flex vertical gap="middle" align="center" className="py-4">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No checklist items"
                />
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ name: "" })}
                >
                  Checklist Item
                </Button>
              </Flex>
            )
          }

          return (
            <>
              <DndContext
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEndWithMove}
              >
                <SortableContext
                  items={fields.map((_, i) => `${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <Flex vertical gap="small">
                    {fields?.map((f, itemIndex) => (
                      <SortableItem key={f?.key} id={`${itemIndex}`}>
                        <Form.Item<ProjectFormDataPhase["checklist_data"]>
                          noStyle
                          name={[f.name, "name"]}
                          rules={[{ required: true, whitespace: true }]}
                        >
                          <TextInput
                            placeholder="Enter checklist item name"
                            className="flex-1"
                          />
                        </Form.Item>

                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(f.name)}
                        />
                      </SortableItem>
                    ))}
                  </Flex>
                </SortableContext>
              </DndContext>

              {!!errors?.length && (
                <Form.Item>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              )}

              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={() => add({ name: "" })}
                className="mt-3"
              >
                Checklist Item
              </Button>
            </>
          )
        }}
      </Form.List>
    </Card>
  )
}

interface SortableItemProps {
  id: string
  children: React.ReactNode
}

const SortableItem = ({ id, children }: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
          opacity: 0.5
        }
      : {})
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Flex
        gap="small"
        align="center"
        className={
          "border border-border rounded px-2 py-2 bg-white duration-300 cursor-pointer transition-all hover:border-primary/50"
        }
      >
        <Button
          type="text"
          size="small"
          icon={<HolderOutlined />}
          style={{ cursor: "move" }}
          {...attributes}
          {...listeners}
        />

        {children}
      </Flex>
    </div>
  )
}
